import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AgentStepEvent, AgentCompleteEvent } from '../../hooks/useWebSocket';

export interface AgentStep {
  step: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  message: string;
  stepIndex?: number;
  stepTotal?: number;
}

export interface AgentProgress {
  planId: number;
  phase: 'plan' | 'pr' | 'issue' | 'fix';
  steps: AgentStep[];
  feed: string[];
  isComplete: boolean;
  isFailed: boolean;
  prUrl?: string;
  prNumber?: number;
  issueUrl?: string;
  issueNumber?: number;
}

interface AgentProgressState {
  active: AgentProgress | null;
}

const initialState: AgentProgressState = { active: null };

const agentProgressSlice = createSlice({
  name: 'agentProgress',
  initialState,
  reducers: {
    agentStepReceived(state, action: PayloadAction<AgentStepEvent>) {
      const { plan_id, phase, step, status, message, step_index, step_total } = action.payload;

      // Start fresh if a new plan/phase starts, OR if the previous run for this
      // same plan_id+phase already finished — a step event arriving after
      // isComplete was true can only mean a new run just began (e.g. the user
      // re-triggered a fix on the same scan). Without this, a completed or
      // failed run's stale isComplete/isFailed state would leak into the next
      // run's progress display.
      if (
        !state.active ||
        state.active.planId !== plan_id ||
        state.active.phase !== phase ||
        state.active.isComplete
      ) {
        state.active = { planId: plan_id, phase, steps: [], feed: [], isComplete: false, isFailed: false };
      }

      const existing = state.active.steps.findIndex((s) => s.step === step);
      const entry: AgentStep = { step, status, message, stepIndex: step_index, stepTotal: step_total };

      if (existing >= 0) {
        state.active.steps[existing] = entry;
      } else {
        state.active.steps.push(entry);
      }

      state.active.feed.push(message);

      // Recomputed, not sticky — a step that fails and later recovers (retry)
      // must be able to clear isFailed, not just ever set it true.
      state.active.isFailed = state.active.steps.some((s) => s.status === 'failed');
    },

    agentCompleteReceived(state, action: PayloadAction<AgentCompleteEvent>) {
      const { plan_id, phase, pr_url, pr_number, issue_url, issue_number } = action.payload;
      if (!state.active || state.active.planId !== plan_id || state.active.phase !== phase) return;
      state.active.isComplete = true;
      if (pr_url) state.active.prUrl = pr_url;
      if (pr_number) state.active.prNumber = pr_number;
      if (issue_url) state.active.issueUrl = issue_url;
      if (issue_number) state.active.issueNumber = issue_number;
    },

    clearAgentProgress(state) {
      state.active = null;
    },
  },
});

export const { agentStepReceived, agentCompleteReceived, clearAgentProgress } = agentProgressSlice.actions;
export default agentProgressSlice.reducer;
